import React from 'react';

/**
 * Shared helper components for the quarterly report pages.
 */

/* ---- Whitespace preservation ----
 * The TipTap editor (ProseMirror) renders with white-space: pre-wrap, so runs of
 * spaces typed in Edit Mode are visible there. Static HTML collapses them to one
 * space, which made those edits look lost in View Mode and the PDF. Convert each
 * run of 2+ spaces inside text (never inside tags) to alternating nbsp/space so
 * the run keeps its width and can still wrap.
 */
export const preserveSpaces = (html) => {
  if (!html || html.indexOf('  ') === -1) return html;
  return html.replace(/(<[^>]*>)|([^<]+)/g, (m, tag, text) => {
    if (tag) return tag;
    return text.replace(/ {2,}/g, (run) =>
      Array.from(run, (_, i) => (i % 2 === 0 ? ' ' : ' ')).join(''));
  });
};

/* ---- Paragraph renderer ---- */
export const TextWithParagraphs = ({ text, className = 'section-text' }) => {
  if (!text) return null;

  // If content contains HTML tags (from TipTap), render directly
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: preserveSpaces(text) }} />;
  }

  // Legacy plain-text fallback
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, i) => (
    <p key={i} className={className}>
      {para.split('\n').map((line, j, arr) => (
        <React.Fragment key={j}>
          {line}
          {j < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  ));
};

/* ---- Pull-quote gutter (left margin) ---- */
export const PullQuoteGutter = ({ callouts }) => {
  if (!callouts || callouts.length === 0) return null;
  return (
    <div className="qr-gutter">
      {callouts.map((c, i) => (
        <div
          key={i}
          className="qr-pull-quote"
          style={{ position: 'absolute', top: `${c.top || 0}mm` }}
        >
          {c.text}
        </div>
      ))}
    </div>
  );
};

/* ---- Footer ---- */
export const ReportFooter = ({ quarterDisplay, quarter, page }) => {
  const isMonthly = quarter && !quarter.includes('-Q');
  const label = isMonthly ? 'Monthly Update' : 'Quarterly Review';
  return (
    <footer className="qr-footer">
      <span className="qr-footer-left">{label} | {quarterDisplay}</span>
      <span className="qr-footer-right">Page {page}</span>
    </footer>
  );
};

/* ---- H1: uppercase with border-bottom rule ---- */
export const SectionHeadingH1 = ({ children }) => (
  <h2 className="qr-h1">{children}</h2>
);

/* ---- H2: bold, no border ---- */
export const SectionHeadingH2 = ({ children }) => (
  <h3 className="qr-h2">{children}</h3>
);

/* ---- Chart caption: small uppercase ---- */
export const ChartCaption = ({ children }) => (
  <p className="qr-chart-caption">{children}</p>
);
